import { ReactNode } from "react";

/**
 * Music Theory–specific layout primitives. They visually separate from the
 * Piano Foundation course's amber/blue Insight callouts and use editorial
 * serif type so the course feels like a book, not a slide deck.
 */

interface ChildrenProps { children: ReactNode }

/** Big chapter-style heading with eyebrow + title in serif. */
export function Chapter({
  number,
  eyebrow,
  title,
  children,
}: {
  number?: number | string;
  eyebrow?: string;
  title: string;
  children?: ReactNode;
}) {
  return (
    <section className="mx-auto w-full max-w-5xl px-4 py-8 lg:px-8 lg:py-12">
      <div className="flex items-baseline gap-4">
        {number != null && (
          <div className="mt-serif text-5xl font-bold text-amber-500/40">
            {String(number).padStart(2, "0")}
          </div>
        )}
        <div className="flex-1">
          {eyebrow && (
            <div className="text-xs font-semibold uppercase tracking-[0.22em] text-amber-400">
              {eyebrow}
            </div>
          )}
          <h2
            className="mt-1 text-3xl font-semibold leading-tight text-nsm-ink lg:text-4xl"
            style={{ fontFamily: '"Cormorant Garamond", "Spectral", Georgia, serif', letterSpacing: "0.005em" }}
          >
            {title}
          </h2>
        </div>
      </div>
      {children && <div className="prose-nsm mt-5 space-y-4">{children}</div>}
    </section>
  );
}

/** A flowing prose section without a title — standard text container. */
export function Prose({ children }: ChildrenProps) {
  return (
    <section className="mx-auto w-full max-w-5xl px-4 py-3 lg:px-8">
      <div className="prose-nsm space-y-4">{children}</div>
    </section>
  );
}

/** Theory-grade callout — uses a richer border + serif label. */
export function TheoryBox({
  label = "The theory",
  children,
}: { label?: string; children: ReactNode }) {
  return (
    <aside className="my-5 rounded-2xl border border-amber-500/30 bg-gradient-to-br from-amber-500/[0.05] to-purple-500/[0.05] p-5">
      <div className="mb-2 flex items-center gap-2 mt-serif text-[15px] italic text-amber-300">
        <span className="text-base not-italic">✦</span>
        {label}
      </div>
      <div className="text-[14px] leading-relaxed text-nsm-ink">{children}</div>
    </aside>
  );
}

/** Pull quote in serif italic. */
export function MTQuote({ children, by }: { children: ReactNode; by?: string }) {
  return (
    <blockquote className="my-6 border-l-2 border-amber-500/60 pl-5">
      <div className="mt-serif text-2xl italic leading-snug text-nsm-ink">
        "{children}"
      </div>
      {by && (
        <div className="mt-2 text-[11px] uppercase tracking-[0.18em] text-nsm-dim">
          — {by}
        </div>
      )}
    </blockquote>
  );
}

/** YouTube video embed for "augment with this" links. */
export function YouTubeEmbed({
  videoId,
  title,
  caption,
}: { videoId: string; title?: string; caption?: string }) {
  return (
    <figure className="my-5 overflow-hidden rounded-2xl border border-nsm-border bg-zinc-900/40">
      <div className="aspect-video w-full">
        <iframe
          src={`https://www.youtube-nocookie.com/embed/${videoId}`}
          title={title ?? "Theory video"}
          className="h-full w-full"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
          allowFullScreen
        />
      </div>
      {caption && (
        <figcaption className="border-t border-nsm-border p-3 text-[12px] italic text-nsm-dim">
          {caption}
        </figcaption>
      )}
    </figure>
  );
}

/** Inline glossary — rich colour, designed for definition lists. */
export function Glossary({ children }: ChildrenProps) {
  return (
    <dl className="my-4 grid gap-2 rounded-xl border border-nsm-border bg-zinc-900/40 p-4 sm:grid-cols-2">
      {children}
    </dl>
  );
}

export function Term({ name, def }: { name: string; def: ReactNode }) {
  return (
    <div className="border-l-2 border-amber-500/40 pl-3">
      <dt className="mt-serif text-[15px] font-semibold text-amber-200">{name}</dt>
      <dd className="mt-0.5 text-[12px] leading-snug text-nsm-dim">{def}</dd>
    </div>
  );
}

/** Big numbered list — for stepwise theory derivations. */
export function Steps({ children }: ChildrenProps) {
  return (
    <ol className="my-4 space-y-3 [counter-reset:mtstep]">
      {children}
    </ol>
  );
}

export function Step({ children }: ChildrenProps) {
  return (
    <li className="flex gap-3 [counter-increment:mtstep] before:mt-0.5 before:flex before:h-6 before:w-6 before:flex-shrink-0 before:items-center before:justify-center before:rounded-full before:bg-amber-500/20 before:mt-serif before:text-sm before:font-bold before:text-amber-300 before:content-[counter(mtstep)]">
      <div className="flex-1 text-[14px] leading-relaxed text-nsm-ink">{children}</div>
    </li>
  );
}
